import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface Staff {
  id: number;
  name: string;
  role: string;
  contact: string;
  email: string;
  isExternal: boolean;
  scheduleCount: number;
}

export default function VendorStudioStaffPage() {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date(2024, 0, 1));
  const [selectedStaff, setSelectedStaff] = useState<number | null>(1);

  const menuItems = [
    { id: 'dashboard', icon: 'ri-dashboard-line', label: '대시보드', path: '/vendor-portal' },
    { id: 'orders', icon: 'ri-file-list-3-line', label: '발주서 관리', path: '/vendor-portal/orders' },
    { id: 'schedule', icon: 'ri-calendar-line', label: '촬영 일정 관리', path: '/vendor-portal/studio-schedule' },
    { id: 'ordersheet', icon: 'ri-file-text-line', label: '촬영 오더시트', path: '/vendor-portal/studio-customers' },
    { id: 'staff', icon: 'ri-team-line', label: '작가·스태프 배정', path: '/vendor-portal/studio-staff' },
    { id: 'photos', icon: 'ri-folder-image-line', label: '촬영 사진 관리', path: '/vendor-portal/studio-photos' },
    { id: 'collaboration', icon: 'ri-user-star-line', label: '플래너 협업 관리', path: '/vendor-portal/studio-requests' },
    { id: 'invoice', icon: 'ri-file-text-line', label: '인보이스 관리', path: '/vendor-portal/invoice' },
    { id: 'tax', icon: 'ri-file-list-line', label: '세금계산서 관리', path: '/vendor-portal/tax' },
    { id: 'products', icon: 'ri-shopping-bag-line', label: '상품 관리', path: '/vendor-portal/products' }
  ];

  const staff: Staff[] = [
    {
      id: 1,
      name: '박작가',
      role: '스냅 작가',
      contact: '010-1111-2222',
      email: 'park@studio.com',
      isExternal: false,
      scheduleCount: 8
    },
    {
      id: 2,
      name: '최작가',
      role: '스냅 작가',
      contact: '010-3333-4444',
      email: 'choi@studio.com',
      isExternal: false,
      scheduleCount: 6
    },
    {
      id: 3,
      name: '정작가',
      role: '영상 작가',
      contact: '010-5555-6666',
      email: 'jung@studio.com',
      isExternal: false,
      scheduleCount: 5
    },
    {
      id: 4,
      name: '김보조',
      role: '보조 작가',
      contact: '010-7777-8888',
      email: 'kim@studio.com',
      isExternal: false,
      scheduleCount: 10
    },
    {
      id: 5,
      name: '이외주',
      role: '외주 작가',
      contact: '010-9999-0000',
      email: 'lee@external.com',
      isExternal: true,
      scheduleCount: 3
    }
  ];

  const schedules = [
    { id: 1, date: '2024-01-25', time: '10:00', customer: '김지은', type: '리허설', staffId: 1 },
    { id: 2, date: '2024-01-25', time: '14:00', customer: '이민수', type: '본식스냅', staffId: 2 },
    { id: 3, date: '2024-01-26', time: '11:00', customer: '박영희', type: '본식영상', staffId: 3 },
    { id: 4, date: '2024-01-28', time: '09:00', customer: '최수진', type: '리허설', staffId: 1 },
    { id: 5, date: '2024-01-28', time: '15:00', customer: '정민호', type: '본식스냅', staffId: 1 }
  ];

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatDate = (year: number, month: number, day: number) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const getSchedulesForStaffAndDate = (staffId: number, dateString: string) => {
    return schedules.filter(schedule => 
      schedule.staffId === staffId && schedule.date === dateString
    );
  };

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const renderCalendar = () => {
    if (!selectedStaff) return null;

    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];
    const weekDays = ['일', '월', '화', '수', '목', '금', '토'];

    const weekHeader = weekDays.map(day => (
      <div key={day} className="p-2 text-center font-medium text-gray-600 bg-gray-50">
        {day}
      </div>
    ));

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="p-2 h-24 border border-gray-200"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateString = formatDate(currentDate.getFullYear(), currentDate.getMonth(), day);
      const daySchedules = getSchedulesForStaffAndDate(selectedStaff, dateString);
      const isToday = dateString === new Date().toISOString().split('T')[0];

      days.push(
        <div
          key={day}
          className={`p-2 h-24 border border-gray-200 ${
            isToday ? 'bg-blue-50' : ''
          } ${daySchedules.length > 0 ? 'bg-yellow-50' : ''}`}
        >
          <div className={`text-sm font-medium mb-1 ${isToday ? 'text-blue-600' : 'text-gray-900'}`}>
            {day}
          </div>
          <div className="space-y-1">
            {daySchedules.map((schedule) => (
              <div
                key={schedule.id}
                className="text-xs p-1 rounded truncate bg-blue-100 text-blue-800"
              >
                {schedule.time} {schedule.customer}
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="grid grid-cols-7">
          {weekHeader}
          {days}
        </div>
      </div>
    );
  };

  const selectedStaffData = staff.find(s => s.id === selectedStaff);

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* 사이드바 */}
      <aside className={`${isSidebarOpen ? 'w-64' : 'w-20'} bg-white border-r border-gray-200 transition-all duration-300 flex flex-col`}>
        {/* 로고 영역 */}
        <div className="p-6 border-b border-gray-200">
          {isSidebarOpen ? (
            <div>
              <h1 className="text-xl font-bold text-gray-800">메이스튜디오</h1>
              <p className="text-sm text-gray-600 mt-1">업체 포털</p>
            </div>
          ) : (
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold">M</span>
            </div>
          )}
        </div>

        {/* 메뉴 */}
        <nav className="flex-1 overflow-y-auto py-4">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center px-6 py-3 text-left hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer ${
                item.path === '/vendor-portal/studio-staff' ? 'bg-blue-50 text-blue-600 border-r-2 border-blue-600' : 'text-gray-700'
              }`}
            >
              <i className={`${item.icon} text-lg ${isSidebarOpen ? 'mr-3' : ''}`}></i>
              {isSidebarOpen && <span>{item.label}</span>}
            </button>
          ))}
        </nav>

        {/* 하단 사용자 정보 */}
        <div className="p-4 border-t border-gray-200">
          {isSidebarOpen ? (
            <div>
              <div className="flex items-center mb-3">
                <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center mr-3">
                  <i className="ri-user-line text-gray-600"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">김대표</p>
                  <p className="text-xs text-gray-600">메이스튜디오</p>
                </div>
              </div>
              <button className="w-full px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 whitespace-nowrap cursor-pointer">
                <i className="ri-logout-box-line mr-2"></i>로그아웃
              </button>
            </div>
          ) : (
            <button className="w-full p-2 text-gray-700 hover:bg-gray-100 rounded-lg cursor-pointer">
              <i className="ri-logout-box-line text-lg"></i>
            </button>
          )}
        </div>
      </aside>

      {/* 메인 컨텐츠 */}
      <div className="flex-1 flex flex-col">
        {/* 헤더 */}
        <header className="bg-white border-b border-gray-200 px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="mr-4 p-2 text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer"
              >
                <i className="ri-menu-line text-xl"></i>
              </button>
              <h2 className="text-xl font-semibold text-gray-900">작가·스태프 배정</h2>
            </div>
            <div className="flex items-center gap-4">
              <button className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer">
                <i className="ri-notification-line text-xl"></i>
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              </button>
            </div>
          </div>
        </header>

        {/* 컨텐츠 영역 */}
        <main className="flex-1 overflow-y-auto">
          <div className="p-6">
            <div className="mb-6">
              <p className="text-sm text-gray-600 mt-1">작가별 스케줄을 확인하고 배정을 관리하세요</p>
            </div>

            <div className="grid grid-cols-12 gap-6">
              {/* 작가 목록 */}
              <div className="col-span-3">
                <div className="bg-white rounded-lg shadow-sm p-4">
                  <h2 className="text-lg font-bold text-gray-800 mb-4">작가 목록</h2>
                  <div className="space-y-2">
                    {staff.map((person) => (
                      <div
                        key={person.id}
                        onClick={() => setSelectedStaff(person.id)}
                        className={`p-3 rounded-lg cursor-pointer transition-colors ${
                          selectedStaff === person.id
                            ? 'bg-blue-50 border-2 border-blue-500'
                            : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                              <i className="ri-user-line text-blue-600"></i>
                            </div>
                            <div>
                              <div className="font-medium text-gray-800">{person.name}</div>
                              <div className="text-xs text-gray-500">{person.role}</div>
                            </div>
                          </div>
                          {person.isExternal && (
                            <span className="text-xs px-2 py-1 bg-orange-100 text-orange-700 rounded-full">
                              외주
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-600">
                          이번 달 일정: {person.scheduleCount}건
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* 작가 캘린더 */}
              <div className="col-span-9">
                {selectedStaffData && (
                  <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                        <i className="ri-user-line text-2xl text-blue-600"></i>
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-gray-800">{selectedStaffData.name}</h2>
                        <p className="text-sm text-gray-600">{selectedStaffData.role}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="text-xs text-gray-600 mb-1">연락처</div>
                        <div className="text-sm font-medium text-gray-800">{selectedStaffData.contact}</div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="text-xs text-gray-600 mb-1">이메일</div>
                        <div className="text-sm font-medium text-gray-800">{selectedStaffData.email}</div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="text-xs text-gray-600 mb-1">이번 달 일정</div>
                        <div className="text-sm font-medium text-blue-600">{selectedStaffData.scheduleCount}건</div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center gap-4">
                    <button
                      onClick={goToPreviousMonth}
                      className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                    >
                      <i className="ri-arrow-left-line text-gray-600"></i>
                    </button>
                    <h2 className="text-xl font-semibold text-gray-800">
                      {currentDate.getFullYear()}년 {currentDate.getMonth() + 1}월
                    </h2>
                    <button
                      onClick={goToNextMonth}
                      className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                    >
                      <i className="ri-arrow-right-line text-gray-600"></i>
                    </button>
                  </div>
                </div>

                {renderCalendar()}

                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <i className="ri-information-line text-blue-600 text-xl"></i>
                    <div>
                      <p className="text-sm font-medium text-blue-900 mb-1">일정 충돌 방지</p>
                      <p className="text-sm text-blue-700">같은 시간대에 중복 배정 시 자동으로 경고가 표시됩니다.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}